/**
 * The "Analyzing" indicator, expanded into a live view of the work behind it.
 *
 * Three tiers, one chevron and one "Details" toggle:
 *
 *  - **Collapsed**: the comb, one line of plain English derived from the
 *    newest thing that actually happened — or, where Stage A offered a word for
 *    what the note is *about*, that word in place of the mechanism's name (see
 *    `headline` for the whole ordering). The comb is one indeterminate cell in
 *    every state; `CombFill`'s phased mode is deliberately not used here.
 *  - **Expanded**: one row per step in start order, each pending / active /
 *    done / error, naming what that step looked up — "Checking for places:
 *    “McCarren Park”, “Peter's”". A few values, then `+N more`, which is a
 *    **control that opens in place**, because this is the tier a reader who is
 *    not debugging a parse opens and it has to tell the whole story there. A
 *    list the *server* clipped is counted in prose instead, because a control
 *    has to open onto what it counted and those names are on no tier. The
 *    status cell says how much the step's plan emitted, as a count; the mark at
 *    the row's head is what reports pending, running, done and failed — or, on
 *    a row the parse was **told to skip**, a dash and the sentence saying why.
 *    The only sub-line is a call that failed. Beneath the steps sits the
 *    **outcome**:
 *    one row per kind of thing the parse created — "Found new people: …". It
 *    comes from the funnel rather than from any loop, because creating a row
 *    is not a loop fact. What the parse *saved* is deliberately not here: the
 *    chips under the note already are the saved values, and a row restating
 *    them was this panel competing with the product (reported 2026-09-21).
 *  - **Technical**: everything above, **plus** the machinery — a routing
 *    sub-line under each step's own row carrying route key, model,
 *    `EffortChip`, iteration counts, durations and token counts for an admin,
 *    and a row per tool call whose arguments *and* whose result each open into
 *    the full list behind them. Cumulative rather than alternative since
 *    2026-09-23: this tier used to replace the step's row, which made the
 *    deepest tier the one that did not say what a step looked up or found.
 *    The "Details" toggle is per panel, and sticky across parses
 *    and reloads (`hooks/useTechnicalTier`): someone who wants the call tree
 *    wants it every time rather than once, so it decides what the *next* panel
 *    opens at — but it no longer reaches back into panels already on screen.
 *
 * **What the technical tier may show is settled, and narrower than "everything
 * the server knows"** — see `docs/parse_activity.md`. Tool arguments, routing
 * and counts, never a prompt and never a raw model response. The fold never
 * carries those fields, and nothing here should start asking for them: the
 * audience for a prompt is an admin debugging a parse, and they have a better
 * view of it in `LlmUsagePage`. **Token and cache counts have the same
 * audience**, and since 2026-09-18 the same gate: they are carried and stored
 * as before, and rendered only for `isAdmin`.
 *
 * **A finished parse renders through this same panel**, from the same fold:
 * `useParseActivity.reviewParse` reads the stored events and folds them into a
 * record marked `review`, which changes the collapsed line and nothing else.
 * Whether the events arrived over a socket or out of a table is not something
 * any of the three tiers knows.
 *
 * **Two surfaces mount this, not one.** The composer hangs it off a check-in's
 * "Analyzing" indicator; Genie hangs it off its turn indicator and off the
 * cards for check-ins a turn just created. Both fold the same events with
 * `hooks/useParseActivity`, so "the same panel" is literal — there is one fold
 * and one set of tiers, and a Genie loop reads the way a parse loop does
 * because it goes through the same table below.
 *
 * **This file is the only place the event vocabulary becomes English.** The
 * fold in `hooks/useParseActivity.ts` transcribes and maps nothing on purpose,
 * so that Genie can mount the same fold for its own loops; the tables below
 * are what a reader actually sees, and an unmapped key degrades to a
 * humanized version of itself rather than to a blank row. A later surface
 * adds its route keys and tool names to these tables and gets the whole panel.
 */

import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import CombFill from '../CombFill'
import EffortChip from '../EffortChip'
import { UserContext } from '../UserContext'
import type {
    ParseActivity,
    ParseCreatedItem,
    ParseStep,
    ParseStepValue,
    ParseToolCall,
    ParseValueVia,
    ParseWriteAction,
} from '../../hooks/useParseActivity'
import { setTechnicalTier } from '../../hooks/useTechnicalTier'

// --- The label tables -------------------------------------------------------

interface StepPhrases {
    /** The expanded row before it has anything to name. "Checking for places". */
    title: string
    /** The collapsed headline while this step runs: a verb phrase. */
    running: string
    /**
     * The same verb phrase once the step has finished. A record read back out
     * of the store renders every step done, and "Checking for places: …" over
     * work that ended yesterday is the same small lie `headline` refuses to
     * tell. A step that *errored* keeps the present tense — see `stepSentence`.
     */
    done: string
}

/**
 * Keyed by the step's **phrase key** (`stage:<name>` / `loop:<route key>`)
 * rather than by the bare route key, so a stage and a loop can never collide
 * and so the lookup needs no second argument. It is deliberately not the step's
 * own `key`, which is unique per loop: two loops under one route key are two
 * rows that read with the same phrases. Genie's route keys go in here beside
 * these.
 *
 * A phrase may carry one `{subject|default}` slot — the name of the thing the
 * loop was resolving, which the server sends on a loop event that has one.
 * The two user-global system features deliberately send none, so their rows
 * read as the phrase already words them; a row that had nothing to add reads
 * exactly as it did before.
 */
const STEP_PHRASES: Record<string, StepPhrases> = {
    'stage:detect': {
        title: 'Matching up projects',
        running: 'Matching up projects',
        done: 'Matched up projects',
    },
    'stage:extract': {
        title: 'Pulling out the details',
        running: 'Pulling out the details',
        done: 'Pulled out the details',
    },
    'loop:checkin_project_detection': {
        title: 'Which projects this is about',
        running: 'Working out which projects this is about',
        done: 'Worked out which projects this is about',
    },
    // People and Places take the subject too, because a project's own
    // people-reference feature routes exactly like the global one: "Checked
    // for relationship candidates" beside "Checked for people" is the pair
    // this is for. The two global features send no subject at all, so these
    // defaults are what their rows read.
    'loop:checkin_feature_extraction_people': {
        title: 'Checking for {subject|people}',
        running: 'Checking for {subject|people}',
        done: 'Checked for {subject|people}',
    },
    'loop:checkin_feature_extraction_places': {
        title: 'Checking for {subject|places}',
        running: 'Checking for {subject|places}',
        done: 'Checked for {subject|places}',
    },
    // The row this slot exists for. Every collection-reference feature runs
    // under the one route key `checkin_feature_extraction_collection`, so the
    // key can only ever say "your lists"; the loop's own `subject` is what
    // turns that into "Checked Piano Pieces".
    'loop:checkin_feature_extraction_collection': {
        title: 'Checking {subject|your lists}',
        running: 'Checking {subject|your lists}',
        done: 'Checked {subject|your lists}',
    },
    // No slot: the scalar batch resolves every scalar feature at once and has
    // no single thing to name. "Read values from your note" was the only label
    // carrying a tail, which made it read as a different kind of sentence
    // beside its neighbours (reported 2026-09-20).
    'loop:checkin_feature_extraction_scalar': {
        title: 'Reading values',
        running: 'Reading values',
        done: 'Read values',
    },
    // Genie's own loops. A Genie turn arms the same sink around itself, so what
    // shows up here is its *thinking* — the bulk-edit compiler working out which
    // check-ins a request selects, the reviser rewriting a draft — and not only
    // the parses a turn happens to trigger. Same table, same rows, same tiers.
    'loop:bulk_edit_compile': {
        title: 'Working out the changes',
        running: 'Working out the changes',
        done: 'Worked out the changes',
    },
    'loop:genie_revise_draft': {
        title: 'Revising the changes',
        running: 'Revising the changes',
        done: 'Revised the changes',
    },
    // Chats' turn loop (docs/chats.md §7). One per turn, under the role.
    'loop:chat.main': {
        title: 'Honey is replying',
        running: 'Honey is replying',
        done: 'Honey replied',
    },
}

interface ToolPhrases {
    /** In flight: "Looking up “Bar Rumba”…" */
    running: string
    /** Resolved: "Looked up “Bar Rumba”" */
    done: string
    /**
     * Which arguments name the thing being acted on, best first. Everything
     * the parser's tools take is either a batch of free-text queries or one
     * short scalar, so the subject is nearly always there to be found.
     */
    subjectArgs: string[]
}

const TOOL_PHRASES: Record<string, ToolPhrases> = {
    place_lookup: {
        running: 'Looking up',
        done: 'Looked up',
        subjectArgs: ['queries', 'query', 'near'],
    },
    collection_items_search: {
        running: 'Searching your lists for',
        done: 'Searched your lists for',
        subjectArgs: ['queries', 'query'],
    },
    checkin_text_search: {
        running: 'Searching your past notes for',
        done: 'Searched your past notes for',
        subjectArgs: ['query', 'queries'],
    },
    get_checkin: {
        running: 'Opening an earlier note',
        done: 'Read an earlier note',
        subjectArgs: [],
    },
    get_checkin_window: {
        running: 'Reading the days around',
        done: 'Read the days around',
        subjectArgs: ['start_date', 'end_date'],
    },
    calculator: {
        running: 'Working out',
        done: 'Worked out',
        subjectArgs: ['expression'],
    },
    // Genie's authoring loops reach for this one; the other four they use are
    // already above, because the parser reaches for them too.
    query_checkins: {
        running: 'Looking through your notes for',
        done: 'Looked through your notes for',
        subjectArgs: ['text_search'],
    },
    // Chats' read tools (docs/chats.md §3). The ones above it shares are
    // already named.
    find_checkins: {
        running: 'Looking through your check-ins',
        done: 'Looked through your check-ins',
        subjectArgs: [],
    },
    list_projects: { running: 'Listing your projects', done: 'Listed your projects', subjectArgs: [] },
    get_project: { running: 'Opening a project', done: 'Read a project', subjectArgs: [] },
    get_feature: { running: 'Reading a feature', done: 'Read a feature', subjectArgs: [] },
    list_collection_items: { running: 'Reading a list', done: 'Read a list', subjectArgs: [] },
    get_collection_item: { running: 'Opening a list item', done: 'Read a list item', subjectArgs: [] },
    get_summaries: { running: 'Reading your summaries', done: 'Read your summaries', subjectArgs: [] },
    fetch_result: { running: 'Reading the rest', done: 'Read the rest', subjectArgs: [] },
    // The search for tools that aren't on the menu until found
    // (docs/chats.md §3, "Deferred tools").
    find_tools: { running: 'Looking for a tool for', done: 'Looked for a tool for', subjectArgs: ['query'] },
    change_checkins: { running: 'Drafting changes', done: 'Drafted changes', subjectArgs: [] },
    create_project: { running: 'Drafting a project', done: 'Drafted a project', subjectArgs: ['title'] },
    edit_project: { running: 'Drafting a project change', done: 'Drafted a project change', subjectArgs: [] },
    set_project_status: { running: 'Drafting a project change', done: 'Drafted a project change', subjectArgs: [] },
    create_features: { running: 'Drafting features', done: 'Drafted features', subjectArgs: [] },
    edit_feature: { running: 'Drafting a feature change', done: 'Drafted a feature change', subjectArgs: [] },
    delete_feature: { running: 'Drafting a feature change', done: 'Drafted a feature change', subjectArgs: [] },
    create_collection: { running: 'Drafting a list', done: 'Drafted a list', subjectArgs: ['name'] },
    edit_collection: { running: 'Drafting a list change', done: 'Drafted a list change', subjectArgs: [] },
    delete_collection: { running: 'Drafting a list change', done: 'Drafted a list change', subjectArgs: [] },
    add_collection_items: { running: 'Drafting list items', done: 'Drafted list items', subjectArgs: [] },
    edit_summary: { running: 'Drafting a summary change', done: 'Drafted a summary change', subjectArgs: [] },
    change_settings: { running: 'Drafting a settings change', done: 'Drafted a settings change', subjectArgs: [] },
    accept_proposal: { running: 'Approving', done: 'Approved', subjectArgs: [] },
}

/**
 * `checkin_feature_extraction_places` → "Checkin feature extraction places".
 * Deliberately dumb: the point of the fallback is that an unmapped key shows
 * up as itself, legibly, rather than as an empty row that reads like a bug.
 */
const humanizeKey = (raw: string): string => {
    const spaced = raw.replace(/^(stage|loop):/, '').replace(/[_:]+/g, ' ').trim()
    if (!spaced) return 'Working'
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Fill a phrase's one `{subject|default}` slot.
 *
 * A phrase with no slot is returned untouched, whatever the subject is — which
 * is what keeps the loops that have nothing to name (the scalar batch, Genie's
 * own loops) reading exactly as they always have.
 *
 * A subject that only repeated the default used to fall back to it. That rule
 * was decided here, in English, and so it could only ever be spelling: it
 * matched the global People feature against "people" and missed the global
 * Locations feature against "places", leaving one system row generic and its
 * twin reading "Checked for Locations" (reported 2026-09-21). The server
 * decides it now, where the fact lives — the two global features send no
 * subject — and a feature the user titled keeps their capitalisation.
 */
const applySubject = (phrase: string, subject?: string): string =>
    phrase.replace(/\{subject\|([^}]*)\}/g, (_, fallback: string) => {
        const name = (subject || '').trim()
        return name || fallback
    })

/**
 * A step's English. Keyed by the *phrase* key, so two loops that routed the
 * same read with the same words while staying two rows. Falls back through the
 * server's own label — which is already a humanized key when the server did not
 * recognise the route either — before humanizing the key itself.
 */
const stepPhrases = (step: ParseStep): StepPhrases => {
    const mapped = STEP_PHRASES[step.phraseKey]
    if (mapped) {
        return {
            title: applySubject(mapped.title, step.subject),
            running: applySubject(mapped.running, step.subject),
            done: applySubject(mapped.done, step.subject),
        }
    }
    const name = humanizeKey(step.label || step.phraseKey)
    return { title: name, running: name, done: name }
}

/**
 * A skipped row's sentence — the whole of its English, and the one place the
 * panel says what a lock is.
 *
 * Named from the **feature's or project's own title**, never from a route key:
 * a skip has no verb, so `STEP_PHRASES` has nothing to offer it, and
 * synthesizing a key to reach one would re-enter the `humanizeKey` fallback
 * that produced both "Checked for Locations" and the `"133364"` ids. So this
 * consults no phrase table at all.
 *
 * The feature clause covers both lock shapes, because a list lock is signalled
 * by either a `KEEP_VALUE` on a live row or a DELETE sentinel and the user
 * performed the same act for both (`docs/list_tagging.md`). A locked *project*
 * needs its own, since nothing was edited on a value there. A lock Genie holds
 * says so instead, in the padlock's own "Set by Genie" voice.
 *
 * The collapsed scalar row names nothing on the left: it stands for several
 * features at once, and its count — the drawer beside it — is what names them.
 */
const skipSentence = (step: ParseStep): string => {
    const name = (step.subject || '').trim()
    if (step.skip === 'scalars') return 'Read values'
    if (step.skipOwner === 'assistant') return `${name} — skipped because Genie set it`
    if (step.skip === 'project') {
        return `${name} — skipped because you've edited this project directly`
    }
    return `${name} — skipped because you've directly edited the value`
}

const quote = (subject: string): string => `“${subject}”`

/**
 * What each designation reads as, and the order the groups are written in.
 *
 * `recent/common` rather than "roster": the reader has never been told the word
 * roster and does not need to be — what they are being told is that the parse
 * already had this one to hand, because it is recent or it comes up a lot.
 * "lookup" is the other half, and the pair is short enough that neither needs a
 * legend.
 *
 * The undesignated group leads, because it is the one that was never looked up
 * at all — a created item, a scalar — and because a row with nothing but those
 * must read exactly as it did before designations existed. After it the order
 * is **fixed**: roster, then lookups. Two runs that found the same values by
 * the same routes write the same sentence, whatever order the plan happened to
 * list them in.
 */
const VALUE_GROUPS: { via?: ParseValueVia; designation?: string }[] = [
    {},
    { via: 'roster', designation: 'recent/common' },
    { via: 'lookup', designation: 'lookup' },
]

/**
 * The values a step found, as one run of prose with the designation closing
 * each group: `“Chez Jared”, “Sofia” (recent/common), “Jared Black's” (lookup)`.
 *
 * **Once per group, never once per value** (2026-09-22). Tagging each value was
 * the first proposal and repeats the word as many times as there are values,
 * which on a five-person row is five `(recent/common)`s saying one thing. A
 * group with nothing in it contributes nothing at all, rather than an empty
 * label — which is also why a row of undesignated values reads as a plain list.
 */
const valuesSentence = (values: ParseStepValue[]): string =>
    VALUE_GROUPS.map(({ via, designation }) => {
        const members = values.filter((value) => value.via === via)
        if (!members.length) return ''
        const named = members.map((value) => quote(value.name)).join(', ')
        return designation ? `${named} (${designation})` : named
    })
        .filter(Boolean)
        .join(', ')

/**
 * What a call or a step names as the thing it is working on: the values
 * themselves, and how many more the server clipped off before they travelled.
 *
 * Two numbers, deliberately. `values.length` is what a reader can be shown —
 * the `+N more` control promises exactly these — and `omitted` is what no
 * surface can produce, because the names are not on the wire and never were.
 * The panel writes the first as a control and the second as prose.
 */
export interface ToolSubjects {
    values: string[]
    omitted: number
}

/**
 * Every value a tool call names as the thing it is working on, in the order
 * the model wrote them.
 *
 * One argument answers, not a merge of several: `place_lookup` carrying both
 * `queries` and `near` is looking up the queries *near* a point, and listing
 * the point beside them would read as a fifth place. So the first argument
 * that yields any text wins, which is what `subjectArgs` orders for.
 *
 * The overflow count comes from `argsOmitted`, keyed by the same argument —
 * never from the list itself. A server that wrote its overflow *into* the list
 * as `"… +2 more"` is a server telling this function that the step looked up a
 * person by that name, and it duly rendered one (reported 2026-09-23).
 */
const toolSubjects = (call: ParseToolCall): ToolSubjects => {
    const phrases = TOOL_PHRASES[call.tool]
    const keys = phrases ? phrases.subjectArgs : []
    const args = call.args || {}
    const candidates = [...keys, ...Object.keys(args).filter((k) => !keys.includes(k))]

    for (const key of candidates) {
        const value = args[key]
        const omitted = call.argsOmitted?.[key] ?? 0
        if (typeof value === 'string' && value.trim()) {
            return { values: [value.trim()], omitted }
        }
        if (Array.isArray(value)) {
            const texts = value
                .map((v) => (typeof v === 'string' ? v.trim() : ''))
                .filter((v) => Boolean(v))
            if (texts.length) return { values: texts, omitted }
        }
    }
    return { values: [], omitted: 0 }
}

/**
 * The same values collapsed into one short phrase, for the *collapsed* line —
 * which has a single tool call's worth of room and shares it with the comb and
 * the chevron. The expanded row has more room and names them properly; see
 * `stepSentence`.
 */
const toolSubject = (call: ParseToolCall): string | undefined => {
    const { values: texts, omitted } = toolSubjects(call)
    if (!texts.length) return undefined
    // The parser is told to batch, so a places lookup usually carries every
    // mention at once. Naming two and counting the rest keeps the line one line
    // — and the rest includes what the wire clipped, which is as much "not
    // named here" as a value this line simply had no room for.
    const named = texts.slice(0, 2)
    const rest = texts.length - named.length + omitted
    if (!rest) return named.length === 1 ? named[0] : `${named[0]} and ${named[1]}`
    return `${named.join(', ')} and ${rest} more`
}

/** One tool call as a sentence. Never the raw arguments — that is tier 3. */
const toolSentence = (call: ParseToolCall): string => {
    const phrases = TOOL_PHRASES[call.tool]
    const running = call.status === 'running'
    const verb = phrases
        ? running
            ? phrases.running
            : phrases.done
        : humanizeKey(call.tool)
    const subject = toolSubject(call)
    const head = subject ? `${verb} ${quote(subject)}` : verb
    if (running) return head
    if (call.status === 'error') {
        return call.resultSummary ? `${head} — couldn't: ${call.resultSummary}` : `${head} — failed`
    }
    return call.resultSummary ? `${head} — ${call.resultSummary}` : head
}

/**
 * How many looked-up values an expanded step row names before it starts
 * counting the rest.
 *
 * Six, and the same number the server's own result summaries name
 * (`MAX_RESULT_NAMES`), so a row's two halves — what it searched for, what came
 * back — are written at one width rather than two. It was three while these
 * rows still clipped to one line; they wrap now, and a row that fits six names
 * before it starts counting is a row most parses never have to open at all.
 *
 * Clipping by *value* rather than by character (the way `ARGS_CLIP` does) is
 * deliberate: half a place name says less than one fewer place name, and the
 * rest is one control away rather than a tier away.
 */
export const STEP_VALUES_SHOWN = 6

/**
 * Everything the step looked up, across all of its calls, first mention first.
 *
 * Deduplicated because a loop that searched for “Peter's” and then looked it up
 * did one thing, not two, and a row that said so twice would read as a bug.
 */
const stepSubjects = (step: ParseStep): ToolSubjects => {
    const seen = new Set<string>()
    const values: string[] = []
    let omitted = 0
    for (const call of step.tools) {
        const subjects = toolSubjects(call)
        // Summed, not deduplicated: two clipped calls each lost their own
        // entries, and nothing here knows whether they lost the same ones.
        omitted += subjects.omitted
        for (const value of subjects.values) {
            const key = value.toLowerCase()
            if (seen.has(key)) continue
            seen.add(key)
            values.push(value)
        }
    }
    return { values, omitted }
}

/**
 * The expanded row's line: the step, and what it looked up.
 *
 *     Checking for places: “McCarren Park”, “Peter's”
 *
 * This is the tier-2 answer to "what is it doing", and it is *step*-level on
 * purpose: one call per mention is the parser's business, and a row per call
 * repeating a verb the step already said is the noise this replaced. The
 * per-call rows — tool name, raw arguments, timings — are the technical tier's,
 * where a reader who wants them has asked for them.
 *
 * A step with nothing to name keeps its plain title, because "Places:" with an
 * empty list would be worse than "Places".
 */
export const stepSentence = (step: ParseStep, shown: number = STEP_VALUES_SHOWN): string => {
    // Before any tense logic: a skip has no tense, because it never ran.
    if (step.kind === 'skipped') return skipSentence(step)
    const { values, omitted } = stepSubjects(step)
    const phrases = stepPhrases(step)
    // The tense is the step's, values or no values: a reviewed record renders
    // every row finished, and a panel where one row read "Checked for places:
    // …" beside another still "Checking for people" would be claiming that half
    // of a parse that ended yesterday is still going. A step that **errored**
    // keeps the present tense — it was interrupted rather than completed, and
    // its red mark is what says it did not finish.
    const done = step.status === 'done'
    if (!values.length) return done ? phrases.done : phrases.title
    const verb = done ? phrases.done : phrases.running
    const named = values.slice(0, shown)
    // What the event could not carry, as **prose**: the `+N more` beside this
    // is a control, and a control has to open onto what it counted. These names
    // are not in the record at any tier, so counting them there would promise a
    // list nothing can produce. Held back until the named ones are all showing,
    // so the row never carries two different `+N more` at once.
    const clipped = omitted > 0 && named.length === values.length ? ` +${omitted} more` : ''
    return `${verb}: ${named.map(quote).join(', ')}${clipped}`
}

/**
 * How many of a step's values the row is not naming — the `+N more` the reader
 * can open.
 *
 * A separate function rather than a suffix on the sentence because the count is
 * a **control**, not prose: tier 2 is the readable list and the rest has to be
 * reachable there, without dropping into the technical tier for raw arguments
 * (reported 2026-09-20). A string cannot be clicked.
 */
export const stepHiddenCount = (step: ParseStep, shown: number = STEP_VALUES_SHOWN): number =>
    Math.max(0, stepSubjects(step).values.length - shown)

// --- Derivations the panel draws from --------------------------------------

/**
 * The steps worth a row.
 *
 * A parse's extract stage is a *container*: it starts, four loops fan out
 * under it, and it finishes when they do. Rendered as a peer of its own
 * children it reads as a sibling that never finishes, and it holds the comb on
 * its hex for the whole run. So a stage step whose following steps (up to the
 * next stage) include a loop is dropped — the loops beneath it say everything
 * it would. A stage that did the work itself, like `detect`, has no loops after
 * it and stays. Genie emits no stage events at all, so nothing there changes.
 *
 * A **skip** is not a loop, and deliberately does not drop the stage above it.
 * An extract stage that only skipped ran nothing, so there is no set of loops
 * to say what it did — dropping its row there would leave the skips reading as
 * children of the *detect* stage, which is the stage that did happen. Every
 * skip is a row in its own right either way.
 */
export const visibleSteps = (steps: ParseStep[]): ParseStep[] =>
    steps.filter((step, i) => {
        if (step.kind !== 'stage') return true
        for (let j = i + 1; j < steps.length; j += 1) {
            if (steps[j].kind === 'stage') break
            if (steps[j].kind === 'loop') return false
        }
        return true
    })

/**
 * Stage A's flavor word for the step currently running, if there is one.
 *
 * Which word, when Stage A sent several, is decided by `rotation` — a counter
 * the caller advances on a **clock**. It used to be the active step's position
 * in the run, which read as churn and was also a small lie about what the word
 * is: the words describe the *note*, not the step, so indexing them by step
 * made the line look like it was reporting on the work whenever the fan-out
 * changed which loop was newest. Measured on a real parse, that alone swapped
 * the line three times — "Reading…", "Walking…", "Running…" — none of the
 * swaps caused by anything the reader could see (reported 2026-09-22).
 *
 * `headline` stays pure: the counter is an argument, so a test calling it twice
 * with the same inputs still gets the same answer.
 */
const flavorWord = (activity: ParseActivity, rotation: number): string | undefined => {
    const words = activity.statusWords
    if (!words.length) return undefined
    return words[Math.abs(Math.trunc(rotation)) % words.length]
}

/**
 * The collapsed line.
 *
 * Derived from the steps rather than from `activity.currentLabel`: the fold's
 * label is a bare string that has lost whether it named a stage, a loop or a
 * tool, and every table here is keyed by exactly that distinction. The steps
 * carry it, so they are the better source — and the two agree, because the
 * fold sets `currentLabel` from the same events.
 *
 * **A flavor word only ever displaces a mechanism.** Stage A's `status_words`
 * describe the note ("Dancing"); `STEP_PHRASES` describe the parser ("Looking
 * up places"), and trading the second for the first is the whole point of them.
 * A tool in flight is neither — "Looking up “Bar Rumba”…" is a specific true
 * statement about this second of this parse, and no amount of character is
 * worth replacing it with something vaguer. So the order below is: the tool,
 * then the flavor word, then the mechanism. Everything else — an error, a
 * finished run, a run that has reported nothing yet — is unchanged, because a
 * flavor word describing work that is over would be a small lie.
 */
export const headline = (
    activity: ParseActivity,
    idleLabel = 'Analyzing',
    rotation = 0,
): string => {
    // Every step, not just the visible ones: between the extract stage opening
    // and its first loop starting, the container stage is the only true answer
    // to "what is happening", and "Pulling out the details..." beats a blank.
    const active = [...activity.steps].reverse().find((s) => s.status === 'active')
    if (!active) {
        if (activity.status === 'error' || activity.status === 'loop_exhausted') {
            return "Couldn't finish analyzing"
        }
        // A record read back out of the store is not work in progress, and
        // saying "Finishing up..." over a parse that ended yesterday would be a
        // small lie of the same kind a flavor word on a finished run would be.
        // …and neither would saying it over a parse that finished a second ago
        // while the reader watched it. The rule is the run's state, not how the
        // record was opened — the same condition that stills the comb.
        if (activity.steps.length && (activity.review || activity.status !== 'running')) {
            return 'Analysis'
        }
        // `idleLabel` covers only the nothing-has-happened-yet case, which is
        // the one a second surface has its own word for: Genie's turn indicator
        // has always said "Thinking..." or named the tool it is about to run,
        // and a turn that never reaches a tool loop should still say that. Once
        // there are steps, the steps are the better answer on every surface.
        //
        // And with steps but none of them active, the honest answer is **no
        // answer**: an empty string, which `useSteadyLine` reads as "keep what
        // is up". This state is the gap between one stage ending and the next
        // one's loops starting, and it used to say "Finishing up..." — measured
        // at 91ms on one parse and 644ms on another, and wrong on both, since
        // the fan-out had not started rather than ended (reported 2026-09-22).
        // The same state does occur at the tail of a run, where the line the
        // reader keeps for a beat is the last thing that was true, and where
        // `parse_finished` replaces it with "Analysis" a moment later anyway.
        return activity.steps.length ? '' : idleLabel
    }
    const inFlight = [...active.tools].reverse().find((t) => t.status === 'running')
    if (inFlight) return `${toolSentence(inFlight)}`
    const flavor = flavorWord(activity, rotation)
    // **No trailing ellipsis** (reported 2026-09-22). "Analyzing..." spends
    // three characters saying what the spinning comb beside it, the running
    // clock after it and the present tense of the verb itself already say. The
    // only ellipsis this line should ever show is the one the browser puts
    // there when the text genuinely does not fit — which is a fact about the
    // width, not a decoration, and which `truncate` still handles.
    if (flavor) return flavor
    return stepPhrases(active).running
}

/**
 * The right-hand detail on an expanded step row: what the step found, or
 * nothing at all.
 *
 * **Results, not values** (reported 2026-09-22). The count is how many things
 * the step's plan emitted, and until now it said "10 values" — which is what
 * they are once they are written, and not what the reader is looking at. The
 * reader is looking at a *lookup*: a row that names what it searched for and
 * then says how much came back. That a few of those came back without a tool
 * call — resolved straight from the recency/frequency roster this product
 * inlines into the prompt — is a fact about our prompt, not a distinction a
 * reader should have to hold: the roster caches the same collections the
 * tools search, so reading it *is* looking something up. Both halves are
 * results, and the drawer says `Found …` so it can never be mistaken for a
 * second list of arguments.
 *
 * The per-call `N matches: …` one tier down is left alone: that is one tool's
 * own return, and it is counted per call rather than per step.
 *
 * It used to say `waiting`, `thinking…`, `looking…`, `stopped` or `done`
 * whenever there was no count to show, and those words report nothing the row
 * does not already say — the mark at its head says pending, running, finished
 * or failed, and the sentence says what is being looked up. "The filler status
 * words go" (reported 2026-09-20): a row with nothing to report shows nothing
 * in this cell, and an errored step reads as failed through its red mark.
 */
export const stepDetail = (step: ParseStep): string => {
    // A skip has no plan, so it has no results to count. The one number it can
    // carry is how many features a collapsed row stands for, which is what
    // makes that row's drawer openable; a row named after its own feature has
    // said everything it has to say in the sentence.
    if (step.kind === 'skipped') {
        const skipped = step.emitted ?? step.values.length
        return step.skip === 'scalars' && skipped > 0 ? `${skipped} skipped` : ''
    }
    if (step.status !== 'done' || step.emitted === undefined) return ''
    return `${step.emitted} ${step.emitted === 1 ? 'result' : 'results'}`
}

/**
 * The outcome rows: what the parse **created**.
 *
 * Not a step and never was — no loop knows it. And not what the parse *saved*
 * either: the chips under the note are the saved values, rendered better than
 * a sentence can, so a row restating them was this panel competing with the
 * product (reported 2026-09-21). What a chip cannot show is whether the item
 * behind it was matched or invented, and that is what this says.
 *
 * A note that met three new people is **one** act, not three, so the events —
 * one per insert — are grouped here. The grouping is the panel's because this
 * is where English lives; the fold transcribes and knows none.
 */

/** The noun for one group, singular and plural.
 *
 * Chosen from the collection's **system kind**, never from its name: a user
 * who renames "People" must not lose the word "person". Everything else reads
 * `<Collection> item(s)`, which is a **placeholder on purpose** — the right
 * noun for an arbitrary collection is collection metadata we do not have yet
 * and a model will later author. This is the honest stand-in for it, not a
 * pluralizer to extend.
 */
const createdNoun = (item: ParseCreatedItem, count: number): string => {
    const plural = count !== 1
    if (item.kind === 'people') return plural ? 'people' : 'person'
    if (item.kind === 'places') return plural ? 'places' : 'place'
    const name = item.collection.trim()
    const noun = plural ? 'items' : 'item'
    return name ? `${name} ${noun}` : noun
}

/** What groups together: one row per system kind, or per collection for the
 *  rest. Keyed on the kind first so two collections of the same kind cannot
 *  split into two rows saying the same noun. */
const createdGroupKey = (item: ParseCreatedItem): string =>
    item.kind ? `kind:${item.kind}` : `collection:${item.collection}`

interface ParseCreatedGroup {
    key: string
    items: ParseCreatedItem[]
}

/** Group the creations in first-appearance order, so the row order follows the
 *  order the parse actually made things. */
const createdGroups = (created: ParseCreatedItem[]): ParseCreatedGroup[] => {
    const groups: ParseCreatedGroup[] = []
    const byKey = new Map<string, ParseCreatedGroup>()
    created.forEach((item) => {
        const key = createdGroupKey(item)
        const existing = byKey.get(key)
        if (existing) {
            existing.items.push(item)
            return
        }
        const group = { key, items: [item] }
        byKey.set(key, group)
        groups.push(group)
    })
    return groups
}

const createdSentence = (group: ParseCreatedGroup): string =>
    `Found new ${createdNoun(group.items[0], group.items.length)}: ${group.items
        .map((item) => item.value)
        .join(', ')}`

/**
 * Genie's own acts, worded as acts.
 *
 * Its control path dispatches these directly rather than through a tool loop,
 * so they reach the panel as their own event and there is nothing in
 * `STEP_PHRASES` that would fit them: "Looked up" is wrong for something that
 * changed the user's data. An unlisted tool degrades to its humanized name the
 * way every other table here does.
 */
const WRITE_PHRASES: Record<string, string> = {
    create_project: 'Created the project',
    create_feature: 'Created the feature',
    commit_draft: 'Saved the changes',
    undo: 'Undid the last change',
    forget_bulk_edit_journal: 'Forgot the undo history',
}

const writeSentence = (write: ParseWriteAction): string => {
    const verb = WRITE_PHRASES[write.tool] ?? humanizeKey(write.tool)
    return write.subject ? `${verb} ${quote(write.subject)}` : verb
}

// --- Marks ------------------------------------------------------------------

/**
 * How long one `.stepmark-pulse` cycle takes. Mirrors `styles/input.css`, and
 * is only ever used to offset *within* a cycle, so the two drifting apart
 * costs a less even spread and nothing else.
 */
const PULSE_PERIOD_MS = 1400

/**
 * A stable offset into the pulse cycle for the row at `index`, so rows do not
 * breathe in unison.
 *
 * Applied as a **negative** `animation-delay`, which starts the animation
 * already that far in rather than holding the bullet still first — a positive
 * delay would leave a new row dark until its turn came round.
 *
 * Spaced by the golden ratio rather than hashed from the step's key, and not
 * drawn at random. Random is out because a fresh value on every render would
 * make a row re-rendering mid-breath jump, and the rows re-render constantly
 * while a parse runs. A hash was the first version and a real parse showed why
 * it is not enough: nothing stops two keys landing next to each other, and the
 * four loops of an ordinary parse came out 1243ms and 1266ms apart — 23ms, in
 * visible unison. Stepping by an irrational fraction of the cycle spreads any
 * number of rows about as evenly as they can be spread, without needing to know
 * how many there will be.
 */
export const pulseDelayMs = (index: number): number =>
    Math.round((((index * 0.618033988749895) % 1) * PULSE_PERIOD_MS + PULSE_PERIOD_MS) % PULSE_PERIOD_MS)

/**
 * How long a status line must be up before another may replace it.
 *
 * The collapsed line is a ranking of true statements recomputed on every event,
 * and a parse fans out, so several of them are true at once and the newest
 * event wins. Measured on real parses, that produced a line that changed four
 * times in the first two seconds — including `Finishing up...` for **91ms** in
 * the gap between the detect stage ending and the first loop starting, when
 * nothing was finishing up at all (reported 2026-09-22).
 *
 * The dwell is what makes the ranking legible without teaching it about any of
 * those cases: a truth that is only true for 91ms never reaches the screen, and
 * anything that outlives the dwell does.
 */
const LINE_DWELL_MS = 700

/**
 * How often the flavor word moves on.
 *
 * Ten seconds, raised from five (reported 2026-09-22: "we're changing it way
 * too often"). The word describes the *note*, which does not change during a
 * parse, so rotating it faster buys no new information — it only makes the one
 * line a reader is trying to read move under them. A typical parse is 30–130
 * seconds, so this is still two to thirteen words, and the floor is what
 * matters: **the flavor word never changes more than once per 10s.** That holds
 * even when the line leaves the word for a running tool and comes back, because
 * the counter runs on this clock rather than on anything the parse does.
 */
const FLAVOR_ROTATE_MS = 10000

/**
 * The line, held still long enough to read.
 *
 * Shows `next` immediately when the current line has been up for `dwellMs`, and
 * otherwise waits out the remainder and then shows **whatever is current at
 * that moment** — not the value that was pending when the timer started. Three
 * changes inside one dwell window therefore collapse to one, rather than
 * queueing up and replaying as a stutter a beat later.
 *
 * That falls out of the effect rather than needing a ref to chase it: a new
 * `next` re-runs the effect, which clears the pending timer and schedules from
 * the same `shownAt`, so the deadline is unmoved and the value it fires with is
 * the current one. (A first version kept a `latest` ref for this. Mutating it
 * to something else changed no test and no behaviour, which is what said it was
 * redundant.)
 *
 * The first line is never delayed, so a parse still says what it is doing as
 * soon as it knows.
 */
const useSteadyLine = (rawNext: string, runKey: number, dwellMs = LINE_DWELL_MS): string => {
    // An empty `headline` means "nothing better to say right now" rather than
    // "say nothing", so the line simply stays where it is.
    const [shown, setShown] = useState(rawNext)
    const next = rawNext || shown
    const [run, setRun] = useState(runKey)
    const shownAt = useRef(Date.now())
    // A new run owns the line outright. Without this the dwell would hold the
    // *previous* turn's last statement over the first beat of the next one,
    // which Genie's indicator showed the moment this went in: one component,
    // reused across turns, still saying what the last turn had been doing.
    // Adjusted during render rather than in an effect, so no committed frame
    // ever carries the stale line.
    if (run !== runKey) {
        setRun(runKey)
        setShown(next)
        shownAt.current = Date.now()
    }
    useEffect(() => {
        if (next === shown) return
        const waited = Date.now() - shownAt.current
        if (waited >= dwellMs) {
            shownAt.current = Date.now()
            setShown(next)
            return
        }
        const id = setTimeout(() => {
            shownAt.current = Date.now()
            setShown(next)
        }, dwellMs - waited)
        return () => clearTimeout(id)
    }, [next, shown, dwellMs])
    return shown
}

/**
 * The collapsed line's click target: the whole row when there is something to
 * expand into, and a bare layout span when there is not.
 *
 * Two shapes rather than a button that is sometimes disabled, because a parse
 * that has said nothing yet has no details *and no affordance* — a disabled
 * button would still take focus order and still say "button" to a screen
 * reader, over a row that is only ever text.
 *
 * `whileHover`/`whileTap` are named states rather than inline objects so the
 * chevron inside can subscribe through `variants`: motion propagates a
 * parent's gesture state to child motion components by name, which is how the
 * row can be the target while the glyph is the thing that moves.
 */
const ExpandTarget: React.FC<{
    canExpand: boolean
    expanded: boolean
    onToggleExpanded?: (next: boolean) => void
    children: React.ReactNode
}> = ({ canExpand, expanded, onToggleExpanded, children }) => {
    const layout = 'flex items-center gap-2 min-w-0 flex-1'
    if (!canExpand) return <span className={layout}>{children}</span>
    return (
        <motion.button
            type="button"
            onClick={() => onToggleExpanded?.(!expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide parse details' : 'Show parse details'}
            className={`${layout} group cursor-pointer text-left`}
            whileHover="hover"
            whileTap="tap"
            data-testid="parse-activity-toggle"
        >
            {children}
        </motion.button>
    )
}

/**
 * A counter that advances every `everyMs` while `live`. Drives the flavor
 * word's rotation, and nothing else — a clock the *words* run on, kept out of
 * `headline` so that function stays pure.
 */
const useRotation = (live: boolean, everyMs = FLAVOR_ROTATE_MS): number => {
    const [n, setN] = useState(0)
    useEffect(() => {
        if (!live) return
        const id = setInterval(() => setN((prev) => prev + 1), everyMs)
        return () => clearInterval(id)
    }, [live, everyMs])
    return n
}

/**
 * How long the run has taken, in milliseconds.
 *
 * Two clocks, because two questions. While a parse is **live** this is the
 * reader's own wall clock against `startedAt`, so the number moves every second
 * whether or not an event happened to land — events arrive in bursts, and a
 * timer that only advances when one does is not a timer. Once the run is over
 * it is the server's `lastAt`, the newest event's offset from the moment the
 * sink was armed, which is how long the work actually took: a record read back
 * out of the store has a `startedAt` of *when the reader opened it*, so a wall
 * clock there would count the wrong thing from the wrong moment.
 */
export const elapsedMs = (activity: ParseActivity, now: number = Date.now()): number => {
    if (activity.review || activity.status !== 'running') return Math.max(0, activity.lastAt)
    return Math.max(0, now - activity.startedAt)
}

/**
 * `6s`, `59s`, `1m12s`. Seconds below a minute, minutes and seconds above it,
 * and never anything finer: this ticks once a second in the corner of a row,
 * and a decimal place there is noise. The technical tier is where exact
 * durations live, to two places, per step and per call.
 */
export const elapsedLabel = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000))
    if (total < 60) return `${total}s`
    return `${Math.floor(total / 60)}m${total % 60}s`
}

/**
 * Re-render once a second while `live`, so `elapsedMs` has a fresh `now`.
 *
 * Stops the moment the run does, rather than idling a timer under every
 * finished panel on the page — a timeline can hold a lot of rows.
 */
const useSecondTick = (live: boolean): number => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!live) return
        setNow(Date.now())
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [live])
    return now
}

/**
 * The small mark at the head of a step row: pending, active, done or stopped.
 *
 * The active one is a **bullet that breathes**, not a second `CombFill`. It was
 * a comb until 2026-09-18, and watching a real parse that was the wrong call:
 * the row sits inches under the headline comb, and a second droplet falling,
 * filling and revolving beside the first reads as the indicator multiplying
 * itself rather than as one step running. The bullet is the pending mark's
 * twin, lit in honey and oscillating between lit and off — the quietest thing
 * that says "this is the one happening now". `CombFill` is untouched and stays
 * right where it is right: the headline.
 *
 * The oscillation is a CSS keyframe (`.stepmark-pulse`, `styles/input.css`)
 * rather than a `motion` prop, because the house vocabulary is springs for
 * interactions and has nothing that loops. The comb's own loops live in that
 * file for the same reason, and this sits beside them with the same
 * reduced-motion fallback — held lit, since the row must still say which step
 * is running.
 *
 * Each row's bullet is **out of phase** with its neighbours (`seed`), because
 * a parse fans out and three or four rows run at once: in lockstep they read
 * as one blinking thing rather than as several pieces of work (reported
 * 2026-09-22).
 *
 * A **skipped** row gets a dash, and it is chosen by `kind` rather than by any
 * status: work the parse was told not to do did not succeed, so a check would
 * be wrong, and it did not fail either, so the red cross would be worse. The
 * dash is the neutral mark for a row that never started.
 */
/**
 * Every mark occupies the **same 14px box**, whatever glyph is in it.
 *
 * They used to size themselves: a check and a cross are 14px svgs, the dots are
 * 6px with 4px either side — also 14 — but the skip dash was 8px with 2px
 * either side, which is 12. So a skipped row's sentence began two pixels left
 * of every other row's and the left edge of the list visibly stepped
 * (reported 2026-09-22). Centring each glyph in one fixed box makes that
 * impossible to get wrong again, rather than asking the next mark to remember
 * to add up to 14.
 *
 * `h-5` is the text's own line height, and the glyph centres inside it. That
 * is what keeps a mark beside the **first line** of a row that wraps: the rows
 * are `items-start` now, so without a full-line-height box the mark would hang
 * off the top of a three-line row instead of marking where it begins.
 */
const MARK_BOX = 'flex h-5 w-3.5 shrink-0 items-center justify-center'

const StepMark: React.FC<{
    status: ParseStep['status']
    row?: number
    kind?: ParseStep['kind']
}> = ({ status, row, kind }) => {
    if (kind === 'skipped') {
        return (
            <span className={MARK_BOX} aria-hidden="true">
                <span
                    className="h-0.5 w-2 rounded-full bg-gray-300"
                    data-testid="parse-activity-step-mark-skipped"
                />
            </span>
        )
    }
    if (status === 'active') {
        return (
            <span className={MARK_BOX} aria-hidden="true">
                <span
                    data-testid="parse-activity-step-mark-active"
                    className="stepmark-pulse h-1.5 w-1.5 rounded-full bg-honey"
                    // Out of phase with its neighbours. See `pulseDelayMs`.
                    style={row === undefined ? undefined : { animationDelay: `-${pulseDelayMs(row)}ms` }}
                />
            </span>
        )
    }
    if (status === 'done') {
        return (
            <span className={MARK_BOX} aria-hidden="true">
                <svg className="h-3.5 w-3.5 text-honey" viewBox="0 0 20 20" fill="currentColor">
                    <path
                        fillRule="evenodd"
                        d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                        clipRule="evenodd"
                    />
                </svg>
            </span>
        )
    }
    if (status === 'error') {
        return (
            // The status cell used to say "stopped" beside this. It does not
            // any more, so the mark is the whole of how a failed step reads —
            // and this is what a test holds it to.
            <span className={MARK_BOX} aria-hidden="true">
                <svg
                    className="h-3.5 w-3.5 text-danger"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    data-testid="parse-activity-step-mark-error"
                >
                    <path d="M6.3 5a.9.9 0 00-1.3 1.3L8.7 10l-3.7 3.7A.9.9 0 106.3 15L10 11.3 13.7 15a.9.9 0 001.3-1.3L11.3 10 15 6.3A.9.9 0 1013.7 5L10 8.7 6.3 5z" />
                </svg>
            </span>
        )
    }
    return (
        <span className={MARK_BOX} aria-hidden="true">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        </span>
    )
}

// --- The technical tier -----------------------------------------------------

/**
 * The step's own short name: `loop:checkin_feature_extraction_places` →
 * `places`. The full route key sits beside it, so this is the column that makes
 * the tree scannable rather than the one that identifies the route.
 */
const technicalName = (step: ParseStep): string => {
    // A skip has no route, so there is no key to shorten. Naming it after the
    // lock is the honest answer at this tier; its subject sits beside it and
    // says which feature or project.
    if (step.kind === 'skipped') return 'skipped'
    const raw = step.phraseKey.replace(/^(stage|loop):/, '')
    const tail = raw.replace(/^checkin_feature_extraction_/, '')
    return tail || raw || step.phraseKey
}

/** The route key itself, unhumanized — this tier is where it reads as itself.
 *  Two loops that routed the same show the same key here and are told apart by
 *  their subject, which is the honest picture: the route is shared, the work is
 *  not. */
const technicalKey = (step: ParseStep): string =>
    // `skip:feature` / `skip:project` / `skip:scalars` read as themselves: the
    // scope *is* the whole identity of a row that routed nowhere.
    step.kind === 'skipped'
        ? step.phraseKey
        : step.phraseKey.replace(/^(stage|loop):/, '')

const seconds = (ms: number): string => `${(ms / 1000).toFixed(2)}s`

const count = (n: number): string => n.toLocaleString()

/**
 * The right-hand metrics on a technical step row: iterations, wall clock and
 * tokens, each only when the stream actually reported it. A stage that ran one
 * completion has no iteration count and should not claim "0 iters".
 *
 * **Token and cache counts are admin-only** — "cache details is like an admin
 * level detail that we should never show for ordinary users" (2026-09-18).
 * What a reader gets from "900 cached" is a number about our bill, not about
 * their note; an admin reading a parse against `LlmUsagePage` does get
 * something from it. The counts are still *carried* and still *stored*
 * (`docs/parse_activity.md` §1, §7) — this narrows who may see them, not what
 * the stream may say — so the gate lives here, at the one place they become
 * text, and defaults to closed so a caller that forgets to pass it shows
 * nothing rather than everything.
 */
export const technicalMetrics = (step: ParseStep, isAdmin = false): string => {
    // A skip has no duration, no model, no iterations and no tokens, so it has
    // no metrics — and saying so up front is what keeps the logic below from
    // ever reporting a zero as a measurement.
    if (step.kind === 'skipped') return ''
    const parts: string[] = []
    if (step.iterations > 0) {
        parts.push(`${step.iterations} ${step.iterations === 1 ? 'iter' : 'iters'}`)
    }
    if (step.durationMs !== undefined) parts.push(seconds(step.durationMs))
    if (!isAdmin) return parts.join(' · ')
    if (step.promptTokens > 0) {
        parts.push(
            step.cachedTokens > 0
                ? `${count(step.promptTokens)} in (${count(step.cachedTokens)} cached)`
                : `${count(step.promptTokens)} in`,
        )
    }
    if (step.completionTokens > 0) parts.push(`${count(step.completionTokens)} out`)
    return parts.join(' · ')
}

/**
 * A tool row's right-hand cell: `ok · 210ms`, `running`, `error · 1.20s`.
 *
 * Nothing here is gated: an outcome and a wall clock are facts about the
 * reader's own parse. The token and cache counts an admin gate would cover are
 * a step's, not a call's — the stream reports usage per iteration — so there is
 * no figure on this row for `isAdmin` to hide.
 */
const technicalToolMetrics = (call: ParseToolCall): string => {
    const ms = call.durationMs
    const timing = ms === undefined ? '' : ms < 1000 ? `${Math.round(ms)}ms` : seconds(ms)
    return timing ? `${call.status} · ${timing}` : call.status
}

/**
 * How wide an argument line may be before it earns an expander. Chosen to fit
 * the panel at the narrowest layout it renders in; longer than this and the
 * clipped form stops carrying any information at all, which is the point at
 * which a reader needs the full value rather than a wider guess.
 */
export const ARGS_CLIP = 72

/** The arguments as one line of JSON. Never the tool's result — that is a summary. */
export const argsLine = (args: Record<string, unknown>): string => {
    try {
        return JSON.stringify(args) ?? '{}'
    } catch {
        // A value the server sent that will not serialize is a bug on the wire,
        // not a reason to blank the row.
        return '{…}'
    }
}

const argsPretty = (args: Record<string, unknown>): string => {
    try {
        return JSON.stringify(args, null, 2) ?? '{}'
    } catch {
        return '{…}'
    }
}

const clipArgs = (line: string): string =>
    line.length > ARGS_CLIP ? `${line.slice(0, ARGS_CLIP - 1)}…` : line

/**
 * The routing line: route key, model, effort, iterations, durations, tokens.
 *
 * A **sub-line of the step's own row**, not a replacement for it (2026-09-23).
 * The technical tier used to swap this in for `PlainStepRow`, which made the
 * deepest tier the only one that did not say what a step looked up, what it
 * found, how many results it got, or why a skipped row was skipped — a reader
 * who wanted the route key gave up the sentence to get it. The tiers are
 * cumulative now: everything the plain tier says, plus this.
 */
const TechnicalStepHead: React.FC<{ step: ParseStep; isAdmin: boolean }> = ({
    step,
    isAdmin,
}) => {
    const metrics = technicalMetrics(step, isAdmin)
    return (
        // Wrapping, like the plain tier's rows and worse without it: at 390px
        // the model, the effort chip and the metrics claimed the whole line
        // through `ml-auto`, and the route key — the one thing this tier
        // exists to show — was allotted **zero** pixels and rendered as
        // nothing at all (measured 2026-09-22). Wrapping lets the metrics fall
        // to a second line instead of starving the cells to their left.
        <div
            className="flex flex-wrap items-start gap-x-2 gap-y-0.5 min-w-0 pl-6"
            data-testid="parse-activity-step-technical"
        >
            <span className="font-mono text-gray-800 shrink-0">{technicalName(step)}</span>
            <span className="font-mono text-[11px] text-gray-400 min-w-0 break-all">
                {technicalKey(step)}
            </span>
            {/* Without this two loops under one route key are two rows with
                identical left-hand cells. The subject is the only thing that
                tells them apart, and this tier is where a reader is looking
                for exactly that. */}
            {step.subject && (
                <span
                    className="font-mono text-[11px] text-gray-500 min-w-0 break-words"
                    data-testid="parse-activity-subject"
                >
                    {step.subject}
                </span>
            )}
            <span className="ml-auto flex items-center gap-1.5 shrink-0 pl-3">
                {/* Routing is only known for a step the stream announced a model
                    for. Stage A reports none, and an EffortChip reading "none"
                    there would claim a routing decision that was never sent. */}
                {step.model && (
                    <>
                        <span className="font-mono text-gray-600">{step.model}</span>
                        <EffortChip effort={step.effort ?? null} />
                    </>
                )}
                {metrics && <span className="text-gray-400">{metrics}</span>}
            </span>
        </div>
    )
}

/** `… +3 more in "queries"` — what the server clipped out of the arguments,
 *  read off the counts it sent beside them. Rendered under the full JSON so the
 *  deepest tier is never the one place a reader cannot tell a list was cut. */
const omittedArgsLine = (call: ParseToolCall): string => {
    const entries = Object.entries(call.argsOmitted || {}).filter(([, n]) => n > 0)
    if (!entries.length) return ''
    return entries.map(([key, n]) => `… +${n} more in "${key}"`).join(', ')
}

const TechnicalToolRow: React.FC<{
    call: ParseToolCall
    open: boolean
    onToggle: () => void
    resultOpen: boolean
    onToggleResult: () => void
}> = ({ call, open, onToggle, resultOpen, onToggleResult }) => {
    const line = argsLine(call.args)
    const long = line.length > ARGS_CLIP
    const omitted = omittedArgsLine(call)
    // The same division the step rows keep: the items are a prefix, the count
    // is the true one, and the difference is said rather than implied.
    const unnamedResults = Math.max(
        0,
        (call.resultTotal ?? 0) - (call.resultItems?.length ?? 0),
    )
    return (
        <div className="pl-6">
            <div className="flex items-center gap-2 min-w-0">
                <span
                    className={
                        call.status === 'error'
                            ? 'font-mono text-danger shrink-0'
                            : 'font-mono text-gray-700 shrink-0'
                    }
                >
                    {call.tool}
                </span>
                {long ? (
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={open}
                        aria-label={open ? 'Hide full arguments' : 'Show full arguments'}
                        className="font-mono text-gray-500 hover:text-gray-700 cursor-pointer truncate text-left min-w-0"
                        data-testid="parse-activity-args"
                    >
                        {open ? 'arguments ⌃' : clipArgs(line)}
                    </button>
                ) : (
                    <span
                        className="font-mono text-gray-500 truncate min-w-0"
                        data-testid="parse-activity-args"
                    >
                        {line}
                    </span>
                )}
                <span className="ml-auto shrink-0 pl-3 text-gray-400">
                    {technicalToolMetrics(call)}
                </span>
            </div>
            {/* Per row, not per panel: one long `queries` array should not force
                every other call in the tree to grow a block. */}
            {open && (
                <pre
                    className="mt-0.5 whitespace-pre-wrap break-all font-mono text-[11px] text-gray-500"
                    data-testid="parse-activity-args-full"
                >
                    {argsPretty(call.args)}
                    {omitted ? `\n${omitted}` : ''}
                </pre>
            )}
            {call.resultSummary &&
                // The result half of what the arguments already had: a summary
                // that opens into the items behind it. This tier is the one
                // that exists to be the detailed one, and until 2026-09-23 it
                // was the tier where a lookup's answer was a sentence and
                // nothing else — a row naming twenty queries over a count of
                // what they found.
                (call.resultItems?.length ? (
                    <div className="pl-3" data-testid="parse-activity-result">
                        <button
                            type="button"
                            onClick={onToggleResult}
                            aria-expanded={resultOpen}
                            aria-label={
                                resultOpen ? 'Hide the full result' : 'Show the full result'
                            }
                            className="text-left text-gray-400 hover:text-gray-600 cursor-pointer min-w-0"
                            data-testid="parse-activity-result-toggle"
                        >
                            → {call.resultSummary} {resultOpen ? '⌃' : '⌄'}
                        </button>
                        {resultOpen && (
                            <ul
                                className="mt-0.5 font-mono text-[11px] text-gray-500 break-words"
                                data-testid="parse-activity-result-full"
                            >
                                {call.resultItems.map((item, i) => (
                                    <li key={`${i}:${item}`}>{item}</li>
                                ))}
                                {unnamedResults > 0 && <li>… +{unnamedResults} more</li>}
                            </ul>
                        )}
                    </div>
                ) : (
                    <div className="pl-3 text-gray-400" data-testid="parse-activity-result">
                        → {call.resultSummary}
                    </div>
                ))}
        </div>
    )
}

// --- The plain tier's rows --------------------------------------------------

/**
 * One inline disclosure on a step row — the `+N more` that opens the rest of
 * the values a step looked up, and the `N values` that opens what it emitted.
 *
 * Both are the same control and read the same way: a count that can be opened.
 * The micro-interaction is the house one for a plain button, unchanged.
 */
const RowDisclosure: React.FC<{
    label: string
    open: boolean
    onToggle: () => void
    ariaLabel: string
    testId: string
}> = ({ label, open, onToggle, ariaLabel, testId }) => (
    <motion.button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={ariaLabel}
        className="shrink-0 whitespace-nowrap text-gray-400 hover:text-gray-600 cursor-pointer"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        data-testid={testId}
    >
        {label}
    </motion.button>
)

/**
 * A step at the plain tier: the sentence, what it looked up, and what it
 * emitted — all of it reachable **here**, without the technical tier.
 *
 * That is the whole of stage 16 on this row. The first expansion level is the
 * one a reader who is not debugging a parse opens, so a list it clips to three
 * and a count it never spells out are both unfinished sentences (reported
 * 2026-09-20). Each opens in place and closes again; neither replaces the
 * other, because "what this step searched for" and "what it wrote down" are
 * different lists that a real parse regularly disagrees about — three names
 * looked up, two values kept.
 */
const PlainStepRow: React.FC<{
    step: ParseStep
    subjectsOpen: boolean
    onToggleSubjects: () => void
    valuesOpen: boolean
    onToggleValues: () => void
    row?: number
}> = ({ step, subjectsOpen, onToggleSubjects, valuesOpen, onToggleValues, row }) => {
    const hidden = stepHiddenCount(step)
    // What this step concluded: the count, opening into the values themselves.
    // The names come from the parser, which knows what an id in its plan
    // refers to — the panel spent one stage showing "133364" here and one
    // showing nothing at all, and both were the same missing lookup.
    const detail = stepDetail(step)
    // A step that named nothing offers no control: an expander onto an empty
    // list is worse than a plain count. That is the ordinary case for a loop
    // with no namer behind it, Genie's included.
    const canOpenValues = step.values.length > 0
    // What the event could not carry. `emitted` is always the true count and
    // the names are a prefix of it, so the drawer says so rather than implying
    // it listed everything.
    const unnamed = Math.max(0, (step.emitted ?? 0) - step.values.length)
    return (
        <>
            {/* `items-start`, because these lines wrap: a mark centred
                against three lines of names floats in the middle of them
                instead of marking where the row begins. */}
            <div className="flex items-start gap-2 min-w-0">
                <StepMark status={step.status} row={row} kind={step.kind} />
                <span
                    className={[
                        // A skip is `pending` and always will be, but it is
                        // not waiting for anything — it reads as an ordinary
                        // row, in the ordinary colour.
                        step.status === 'pending' && step.kind !== 'skipped'
                            ? 'text-gray-400'
                            : 'text-gray-700',
                        // **Always wrapping** (reported 2026-09-22). These
                        // lines used to clip with an ellipsis until the values
                        // were opened, which on a phone meant most of them
                        // read as "Benzo Taper — skipped because you've e…"
                        // and "Checked for relationship candi…" — the half of
                        // the sentence that says what happened, cut off. A
                        // panel that is now as wide as the note still cannot
                        // fit these on one line at 390px, and there is no
                        // width at which clipping the verb is the right
                        // answer. The `+N more` control beside it still means
                        // what it meant: how many values are *named*, not
                        // whether the row is allowed its own height.
                        'min-w-0 break-words',
                    ].join(' ')}
                    data-testid="parse-activity-step-sentence"
                >
                    {stepSentence(
                        step,
                        subjectsOpen ? Number.POSITIVE_INFINITY : STEP_VALUES_SHOWN,
                    )}
                </span>
                {(hidden > 0 || subjectsOpen) && (
                    <RowDisclosure
                        label={subjectsOpen ? 'less' : `+${hidden} more`}
                        open={subjectsOpen}
                        onToggle={onToggleSubjects}
                        ariaLabel={
                            subjectsOpen
                                ? 'Show fewer of the things this step looked up'
                                : 'Show everything this step looked up'
                        }
                        testId="parse-activity-step-more"
                    />
                )}
                {detail &&
                    (canOpenValues ? (
                        <span className="ml-auto pl-3">
                            <RowDisclosure
                                label={detail}
                                open={valuesOpen}
                                onToggle={onToggleValues}
                                ariaLabel={
                                    step.kind === 'skipped'
                                        ? valuesOpen
                                            ? 'Hide what this parse skipped'
                                            : 'Show what this parse skipped'
                                        : valuesOpen
                                          ? 'Hide what this step found'
                                          : 'Show what this step found'
                                }
                                testId="parse-activity-step-values-toggle"
                            />
                        </span>
                    ) : (
                        <span className="ml-auto pl-3 text-gray-400 shrink-0">{detail}</span>
                    ))}
            </div>
            <AnimatePresence initial={false}>
                {valuesOpen && canOpenValues && (
                    <motion.div
                        key="values"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{ transformOrigin: 'left center' }}
                        className="pl-6 text-gray-500"
                        data-testid="parse-activity-values"
                    >
                        {/* `Skipped` on a skip row, for the same reason the
                            lead-in exists at all: without it the drawer reads
                            as a continuation of the line above, and "Found" on
                            a list of features nothing was read from would be
                            the opposite of true. */}
                        {step.kind === 'skipped' ? 'Skipped' : 'Found'}{' '}
                        {valuesSentence(step.values)}
                        {unnamed > 0 ? ` +${unnamed} more` : ''}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

/**
 * The outcome block: one row per kind of thing the parse created, plus the
 * acts a Genie turn dispatched.
 *
 * It sits under the steps because it is the answer rather than the working —
 * and it renders at **both** tiers, because "what did this do to my account"
 * is not a technical question.
 */
const OutcomeRows: React.FC<{ activity: ParseActivity }> = ({ activity }) => {
    const groups = createdGroups(activity.created)
    if (!groups.length && !activity.writes.length) return null
    return (
        <>
            {activity.writes.map((write, i) => (
                <div
                    key={`write:${i}:${write.tool}`}
                    className="flex items-start gap-2 min-w-0 py-0.5"
                    data-testid="parse-activity-write"
                >
                    <StepMark status="done" />
                    <span className="text-gray-700 min-w-0 break-words">{writeSentence(write)}</span>
                </div>
            ))}
            {groups.map((group) => (
                <div
                    key={`created:${group.key}`}
                    className="flex items-start gap-2 min-w-0 py-0.5"
                    data-testid="parse-activity-created"
                >
                    <StepMark status="done" />
                    <span className="text-gray-700 min-w-0 break-words">
                        {createdSentence(group)}
                    </span>
                </div>
            ))}
        </>
    )
}

/** Add or remove one key from a set of open disclosures. Three of them behave
 *  identically — arguments, looked-up values, emitted values — so the toggle is
 *  written once. */
const toggleIn =
    (setOpen: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>) => (key: string) =>
        setOpen((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

// --- The panel --------------------------------------------------------------

/**
 * Whether this record has anything behind the chevron. Exported because the
 * row around the panel aligns itself differently when the panel is open, and
 * that must turn on the same rule the chevron does.
 */
export const hasParseDetail = (activity: ParseActivity): boolean =>
    visibleSteps(activity.steps).length > 0 ||
    // The outcome counts on its own. A Genie turn that only created a project
    // ran no tool loop, so it has no step rows — and the act it performed is
    // exactly the thing worth opening the panel for.
    activity.created.length > 0 ||
    activity.writes.length > 0

interface ParseActivityPanelProps {
    /** The record for this checkin's parse. Its `expanded` flag is what the
     *  disclosure reads — the state lives with the parse, not in here, so a
     *  re-render or a superseding parse cannot lose it. */
    activity: ParseActivity
    /** Toggle the disclosure. Omitted → no chevron, i.e. the indicator exactly
     *  as it was before this panel existed. A surface that only knows *that*
     *  work is running passes nothing. */
    onToggleExpanded?: (expanded: boolean) => void
    /** The collapsed line while nothing has been reported yet. Defaults to the
     *  composer's "Analyzing". Genie passes its own turn word, so a turn
     *  that runs no tool loop reads exactly as it always has. */
    idleLabel?: string
    /** Called when the expanded rows have finished animating *out*. The row
     *  above this panel top-aligns while the rows are on screen and centres
     *  when they are not, and it cannot tell the difference between "told to
     *  collapse" and "gone" on its own — the drawer's `AnimatePresence` lives
     *  in here. Without this it re-centres against a panel that is still tall,
     *  and the date beside it visibly drops for the length of the exit. */
    onRowsSettled?: () => void
    /**
     * Render the two halves as **separate children of the caller's grid**
     * rather than as one stacked box: the status line into the cell beside
     * whatever leads the row, and the expanded box across the full width
     * beneath it.
     *
     * The read view needs this because the box was sized by its content and
     * came out ~240px wide — unreadable on a phone, and not lined up with
     * anything (reported 2026-09-22). It has to match the check-in body, which
     * means starting at the date's left edge and running to the right edge, and
     * a box nested inside a flex item beside the date cannot do either.
     *
     * `display: contents` is what makes that possible without lifting this
     * component's disclosure state into the caller: the root box stops
     * generating a frame, and its two children become grid items in their own
     * right. The trade is that the root can no longer be animated — a
     * `display: contents` element has no box to transform — which is why the
     * enter animation moved onto the status line itself below, and why the
     * composer and Genie, which stack these two normally, leave this off.
     */
    inGrid?: boolean
    /** Flip this panel's "Details" tier. Omitted → the toggle only remembers
     *  the preference for the next panel, which is what a surface showing a
     *  single panel wants. Passed → the caller owns which record moved, so two
     *  analyses open at once no longer flip together (reported 2026-09-23). */
    onToggleTechnical?: (technical: boolean) => void
}

const ParseActivityPanel: React.FC<ParseActivityPanelProps> = ({
    activity,
    onToggleExpanded,
    idleLabel,
    onRowsSettled,
    inGrid = false,
    onToggleTechnical,
}) => {
    const steps = visibleSteps(activity.steps)
    const rotation = useRotation(activity.status === 'running' && !activity.review)
    const line = useSteadyLine(headline(activity, idleLabel, rotation), activity.startedAt)
    // Token and cache counts are the one thing this panel shows selectively.
    // Nothing else turns on who is reading: a parse's steps, tools and
    // arguments are the reader's own note coming back to them.
    const { user } = React.useContext(UserContext)
    const isAdmin = Boolean(user?.isAdmin)
    // Nothing to expand into until the parse has said something. Before the
    // first event this is byte-for-byte the old indicator.
    // Whether this record describes a parse that is over. A reviewed record
    // always is; a live one is once it reports an outcome. Only the mark turns
    // on it — the words are `headline`'s business.
    const finished = activity.review || activity.status !== 'running'
    // A run with nothing behind it yet has no clock: the stepless stand-in is
    // what a row shows before its socket has said anything, and starting a
    // timer there would time the reader's wait rather than the work.
    const ticking = !finished && activity.steps.length > 0
    const now = useSecondTick(ticking)
    const elapsed =
        ticking || (finished && activity.lastAt > 0)
            ? elapsedLabel(elapsedMs(activity, now))
            : ''
    const canExpand = Boolean(onToggleExpanded) && hasParseDetail(activity)
    const expanded = canExpand && activity.expanded
    const technical = activity.technical
    // Which argument rows are showing their full JSON. Local and per-mount: it
    // is a reading position, not a preference, and it should not follow the
    // reader into the next parse the way the tier itself does.
    const [openArgs, setOpenArgs] = React.useState<ReadonlySet<string>>(new Set())
    // The result half of the same thing, per call and for the same reason: one
    // long result should not force every other row in the tree to grow a list.
    const [openResults, setOpenResults] = React.useState<ReadonlySet<string>>(new Set())
    // The plain tier's two, keyed by step: the rest of what a step looked up,
    // and what it emitted. Local and per-mount for the same reason as
    // `openArgs` — a reading position, not a preference.
    const [openSubjects, setOpenSubjects] = React.useState<ReadonlySet<string>>(new Set())
    const [openValues, setOpenValues] = React.useState<ReadonlySet<string>>(new Set())
    // Plain consts rather than `useCallback`: a state setter is stable, nothing
    // below is memoized, and the only thing a `useCallback` would add here is a
    // dependency array a linter cannot check.
    const toggleArgs = toggleIn(setOpenArgs)
    const toggleResults = toggleIn(setOpenResults)
    const toggleSubjects = toggleIn(setOpenSubjects)
    const toggleValues = toggleIn(setOpenValues)

    return (
        // `data-testid` on the root so a test can count how many panels one
        // check-in is showing: the composer and the read-view header each
        // mount this, and they must never both be on screen at once.
        <div
            className={inGrid ? undefined : 'flex flex-col gap-1 min-w-0'}
            // Not a Tailwind class: `contents` exists there, but this is a
            // structural switch the caller drives, and an inline style keeps
            // the two shapes of this component readable in one place.
            style={inGrid ? { display: 'contents' } : undefined}
            data-testid="parse-activity"
        >
            {/* `min-h-6` matches the date beside it: the collapsed line then sits
                on the same baseline whether the header row is centering or
                top-aligning. */}
            <motion.span
                role="status"
                className={[
                    'text-honey flex items-center gap-2 text-base min-w-0 min-h-6',
                    // Its own row on a phone, beside the date from `sm` up.
                    // See the grid comment in `CheckinItem`.
                    inGrid
                        ? 'col-start-1 col-span-2 row-start-2 sm:col-start-2 sm:col-span-1 sm:row-start-1'
                        : '',
                ].join(' ')}
                // The reveal, which used to sit on a wrapper around this whole
                // component in the read view. Only in grid mode: the composer
                // and Genie wrap this panel in their own, and two would
                // compound. The surface spring, from the same origin as the
                // drawer below — the line arrives the way the region behind
                // its chevron does, rather than in a second vocabulary.
                initial={inGrid ? { opacity: 0, scale: 0.95, y: -8 } : false}
                animate={inGrid ? { opacity: 1, scale: 1, y: 0 } : undefined}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={inGrid ? { transformOrigin: 'top left' } : undefined}
            >
                {/* The whole line is the target, not the chevron (reported
                    2026-09-22). A 16px glyph at the end of a row is a
                    needlessly small thing to hit — worst on a phone, where the
                    row is wide, the chevron is at the far edge and the words
                    beside it look just as clickable. The status, the clock and
                    the chevron read as one control because they behave as one.

                    `role="status"` stays on the span outside, so the live
                    region is not also the button: a control that announces
                    itself on every tick of the clock is a different bug. */}
                <ExpandTarget
                    canExpand={canExpand}
                    expanded={expanded}
                    onToggleExpanded={onToggleExpanded}
                >
                    {/* One cell, in every state. `CombFill` has a phased mode and
                        this used to reach for it once the loop count was known;
                        watching it, an indicator that grows a hex as each tool
                        starts reads as the indicator multiplying itself rather
                        than as progress (reported 2026-09-18). The step list
                        behind the chevron is where a reader counts steps.

                        Still and filled once the parse is over: a spinner over a
                        record of something that already finished says work is
                        happening when none is (reported 2026-09-18). */}
                    <CombFill size={16} current={finished ? 1 : 0} />
                    <span className="text-gray-600 truncate">{line}</span>
                    {/* The clock. Ticking while the work runs, frozen at the total
                        once it is over — which is now a thing a reader sees, since
                        a panel they had open stays open when the parse concludes. */}
                    {elapsed !== '' && (
                        <span
                            className="text-gray-400 shrink-0 tabular-nums"
                            data-testid="parse-activity-elapsed"
                        >
                            {elapsed}
                        </span>
                    )}
                    {canExpand && (
                        <motion.svg
                            className="h-4 w-4 shrink-0 text-gray-400 group-hover:text-gray-600"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                            data-testid="parse-activity-chevron"
                            animate={{ rotate: expanded ? 180 : 0 }}
                            // The house micro-interaction, reached through
                            // variants rather than `whileHover` on this element:
                            // the pointer is over the *row*, so the parent
                            // propagates the state and the glyph is what moves.
                            // Scaling the full-width row instead would shove the
                            // layout around on every hover.
                            variants={{ hover: { scale: 1.1 }, tap: { scale: 0.9 } }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z"
                                clipRule="evenodd"
                            />
                        </motion.svg>
                    )}
                </ExpandTarget>
            </motion.span>

            <AnimatePresence onExitComplete={onRowsSettled}>
                {expanded && (
                    <motion.div
                        key="parse-activity-steps"
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        style={{ transformOrigin: 'top left' }}
                        // `col-span-3` is what makes the box match the
                        // check-in body: it starts at the date's left edge
                        // instead of after it, and runs past the actions menu
                        // to the same right edge. `mt-1` stands in for the flex column's `gap-1`,
                        // which a grid row does not inherit.
                        className={`rounded-md bg-gray-50 px-3 py-2 text-sm ${
                            inGrid
                                ? 'col-span-2 row-start-3 sm:col-span-3 sm:row-start-2 mt-1 w-full min-w-0'
                                : ''
                        }`}
                        data-testid="parse-activity-steps"
                    >
                        <AnimatePresence initial={false}>
                            {steps.map((step, row) => (
                                <motion.div
                                    key={step.key}
                                    initial={{ opacity: 0, scale: 0.6 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.6 }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    style={{ transformOrigin: 'left center' }}
                                    className="py-0.5"
                                    data-testid="parse-activity-step"
                                >
                                    {/* Cumulative, not alternative: the
                                        technical tier adds the routing line
                                        under the step's own row rather than
                                        standing in for it. */}
                                    <PlainStepRow
                                        step={step}
                                        subjectsOpen={openSubjects.has(step.key)}
                                        onToggleSubjects={() => toggleSubjects(step.key)}
                                        valuesOpen={openValues.has(step.key)}
                                        onToggleValues={() => toggleValues(step.key)}
                                        row={row}
                                    />
                                    {technical && (
                                        <TechnicalStepHead step={step} isAdmin={isAdmin} />
                                    )}
                                    <AnimatePresence initial={false}>
                                        {/* The plain tier keeps one sub-line,
                                            and only one: a call that failed.
                                            The step's own line names what it
                                            looked up and its status cell says
                                            how far it got, so a row per
                                            successful call only repeats them —
                                            but neither can say that a lookup
                                            came back broken, and a reader whose
                                            place is missing is owed that. */}
                                        {step.tools
                                            .filter((call) => technical || call.status === 'error')
                                            .map((call) => (
                                                <motion.div
                                                    key={call.callId}
                                                    initial={{ opacity: 0, scale: 0.6 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.6 }}
                                                    transition={{
                                                        type: 'spring',
                                                        stiffness: 500,
                                                        damping: 30,
                                                    }}
                                                    style={{ transformOrigin: 'left center' }}
                                                    className={
                                                        technical
                                                            ? 'min-w-0'
                                                            : call.status === 'error'
                                                              ? 'pl-6 text-danger break-words'
                                                              : 'pl-6 text-gray-500 break-words'
                                                    }
                                                    data-testid="parse-activity-tool"
                                                >
                                                    {technical ? (
                                                        <TechnicalToolRow
                                                            call={call}
                                                            open={openArgs.has(call.callId)}
                                                            onToggle={() => toggleArgs(call.callId)}
                                                            resultOpen={openResults.has(
                                                                call.callId,
                                                            )}
                                                            onToggleResult={() =>
                                                                toggleResults(call.callId)
                                                            }
                                                        />
                                                    ) : (
                                                        toolSentence(call)
                                                    )}
                                                </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </AnimatePresence>

                        <OutcomeRows activity={activity} />

                        {activity.dropped > 0 && (
                            <div className="pt-1 text-gray-400" data-testid="parse-activity-dropped">
                                {activity.dropped} more step
                                {activity.dropped === 1 ? '' : 's'} not shown
                            </div>
                        )}

                        {/* The tier toggle, at the foot rather than the head:
                            the rows are what the reader came for, and this is
                            the control they reach for once, not the one they
                            read past every time. It writes the sticky
                            preference *and* tells the caller: the preference
                            decides what the next panel opens at, the caller
                            decides which record just moved. Writing only the
                            preference is what made two open analyses flip
                            together (reported 2026-09-23). */}
                        <div className="flex justify-end pt-1">
                            <motion.button
                                type="button"
                                onClick={() => {
                                    // Remember it for the next panel either
                                    // way; move *this* one only through the
                                    // caller, which knows which record it is.
                                    setTechnicalTier(!technical)
                                    onToggleTechnical?.(!technical)
                                }}
                                aria-pressed={technical}
                                aria-label={
                                    technical
                                        ? 'Hide technical details'
                                        : 'Show technical details'
                                }
                                className="flex items-center gap-0.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                data-testid="parse-activity-details-toggle"
                            >
                                Details
                                <motion.svg
                                    className="h-3.5 w-3.5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    animate={{ rotate: technical ? 180 : 0 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z"
                                        clipRule="evenodd"
                                    />
                                </motion.svg>
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default ParseActivityPanel
